import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { can, rupees } from "@/lib/helpers";
import api, { apiError } from "@/lib/api";
import {
  Gauge, ShoppingBagOpen, Diamond, Package, CurrencyInr, UsersThree,
  Coins, Percent, ChartBar, UserGear, ShieldCheck, Buildings, ClipboardText,
  GearSix, SignOut, List, X, CaretDown, Megaphone, Handshake, Key, Wallet, Scales, WhatsappLogo,
} from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: Gauge, res: "dashboard", act: "view", exact: true },
  { to: "/pos", label: "POS / Billing", icon: ShoppingBagOpen, res: "pos", act: "view" },
  { to: "/products", label: "Products", icon: Diamond, res: "products", act: "view" },
  { to: "/inventory", label: "Inventory", icon: Package, res: "inventory", act: "view" },
  { to: "/gold-rates", label: "Gold Rates", icon: CurrencyInr, res: "gold_rates", act: "view" },
  { to: "/customers", label: "Scheme Customers", icon: UsersThree, res: "customers", act: "view" },
  { to: "/schemes", label: "Gold Schemes", icon: Coins, res: "schemes", act: "view" },
  { to: "/agents", label: "Agents", icon: Handshake, res: "agents", act: "view" },
  { to: "/expenses", label: "Expenses", icon: Wallet, res: "expenses", act: "view" },
  { to: "/purchases", label: "Purchases", icon: Scales, res: "purchases", act: "view" },
  { to: "/whatsapp", label: "WhatsApp Offers", icon: WhatsappLogo, res: "notifications", act: "manage" },
  { to: "/discounts", label: "Discounts", icon: Percent, res: "discounts", act: "view" },
  { to: "/reports", label: "Reports", icon: ChartBar, res: "reports", act: "view" },
  { to: "/users", label: "Users", icon: UserGear, res: "users", act: "view" },
  { to: "/roles", label: "Roles", icon: ShieldCheck, res: "roles", act: "view" },
  { to: "/branches", label: "Branches", icon: Buildings, res: "branches", act: "view" },
  { to: "/audit", label: "Audit Log", icon: ClipboardText, res: "audit", act: "view" },
];

function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const save = async () => {
    if (nw.length < 6) return toast.error("New password must be at least 6 characters");
    if (nw !== cf) return toast.error("Passwords do not match");
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password changed"); setOpen(false); setCur(""); setNw(""); setCf("");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="change-password-btn" title="Change password"><Key size={18} /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-2xl">Change Password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-[#6B6B6B]">Current Password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1 bg-white border-[#E6E2D6]" data-testid="cp-current" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">New Password</Label><Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} className="mt-1 bg-white border-[#E6E2D6]" data-testid="cp-new" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Confirm New Password</Label><Input type="password" value={cf} onChange={(e) => setCf(e.target.value)} className="mt-1 bg-white border-[#E6E2D6]" data-testid="cp-confirm" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="cp-save">Update Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NoticeBar({ user }) {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const canManage = can(user, "notifications", "manage");
  const load = () => api.get("/notifications/active").then(({ data }) => setNotes(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  const post = async () => {
    if (!msg.trim()) return;
    try { await api.post("/notifications", { message: msg.trim() }); toast.success("Notice posted"); setMsg(""); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => { try { await api.delete(`/notifications/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  if (notes.length === 0 && !canManage) return null;
  return (
    <div className="flex items-center gap-2 bg-[#1A1A1A] text-[#E8Cf9c] px-3 py-1.5 border-b border-[#C5A059]/30" data-testid="notice-bar">
      <Megaphone size={16} weight="fill" className="text-[#C5A059] shrink-0" />
      <div className="flex-1 overflow-hidden">
        {notes.length ? (
          <div className="og-marquee-track text-sm">
            {notes.map((n) => (
              <span key={n.id} className="mr-16" data-testid={`notice-${n.id}`}>
                {n.message}
                {canManage && <button onClick={() => remove(n.id)} className="ml-2 text-[#C5A059] hover:text-white align-middle" data-testid={`notice-remove-${n.id}`}><X size={12} /></button>}
              </span>
            ))}
          </div>
        ) : <span className="text-sm text-[#8A8A8A]">No active notices</span>}
      </div>
      {canManage && (
        <Button size="sm" variant="ghost" className="h-7 text-[#C5A059] hover:text-white shrink-0" onClick={() => setOpen(true)} data-testid="add-notice-btn">+ Notice</Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-2xl">Post a Notice</DialogTitle></DialogHeader>
          <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="e.g. Dhanteras offer: 10% off making charges this week!" className="bg-white border-[#E6E2D6]" data-testid="notice-input" />
          <DialogFooter><Button onClick={post} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="notice-save">Publish to all users</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GoldTicker({ branchId }) {
  const [rates, setRates] = useState(null);
  useEffect(() => {
    api.get(`/gold-rates/current${branchId ? `?branch_id=${branchId}` : ""}`).then(({ data }) => setRates(data?.rates)).catch(() => {});
  }, [branchId]);
  if (!rates) return null;
  return (
    <div className="hidden md:flex items-center gap-3 bg-[#F2EBE1] border border-[#E6E2D6] rounded-md px-3 py-1.5" data-testid="gold-rate-ticker">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-[#8A6D2F]">Live Gold /g</span>
      {Object.entries(rates).map(([k, v]) => (
        <span key={k} className="text-xs tabular-nums text-[#1A1A1A]">
          <span className="text-[#8A8A8A]">{k}</span> {rupees(v).replace(".00", "")}
        </span>
      ))}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, branches, activeBranch, selectBranch } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const items = NAV.filter((n) => can(user, n.res, n.act));

  return (
    <div className="min-h-screen flex bg-[#FDFBF7]">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 h-screen w-64 bg-white border-r border-[#E6E2D6] flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-5 py-5 border-b border-[#E6E2D6] flex items-center justify-between">
          <div>
            <div className="font-display text-2xl font-semibold text-[#C5A059] leading-none tracking-wide">OM GOLD</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] mt-1">Jewellery ERP</div>
          </div>
          <button className="lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close"><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {items.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to} to={n.to} end={n.exact}
                data-testid={`nav-${n.res}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive ? "bg-[#F2EBE1] text-[#8A6D2F] font-medium" : "text-[#4A4A4A] hover:bg-[#FDFBF7]"
                  }`
                }
              >
                <Icon size={18} weight="duotone" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
        <NavLink to="/settings" className="flex items-center gap-3 px-5 py-2.5 text-sm text-[#4A4A4A] hover:bg-[#FDFBF7] border-t border-[#E6E2D6]" data-testid="nav-settings">
          <GearSix size={18} weight="duotone" /> Settings
        </NavLink>
        <div className="px-5 py-3 border-t border-[#E6E2D6] text-[10px] text-[#8A8A8A] leading-relaxed flex items-center gap-2">
          <img src="/microcore-logo.png" alt="MICROCORE IT SOLUTIONS" className="h-8 w-8 object-contain" />
          <span>Developed by<br />
            <a href="https://www.microcoreitsolutions.com/" target="_blank" rel="noopener noreferrer" className="text-[#C5A059] font-semibold tracking-wide hover:underline" data-testid="microcore-link">MICROCORE IT SOLUTIONS</a>
          </span>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white/95 backdrop-blur border-b border-[#E6E2D6] flex items-center justify-between px-4 lg:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open"><List size={22} /></button>
            <GoldTicker branchId={activeBranch} />
          </div>
          <div className="flex items-center gap-3">
            {can(user, "branches", "create") ? (
              <Select value={activeBranch || ""} onValueChange={selectBranch}>
                <SelectTrigger className="w-[180px] h-9 text-sm border-[#E6E2D6]" data-testid="branch-selector">
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id} data-testid={`branch-opt-${b.code}`}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="hidden sm:flex items-center gap-2 h-9 px-3 rounded-md border border-[#E6E2D6] bg-[#F8F6F0] text-sm text-[#4A4A4A]" data-testid="branch-static">
                <Buildings size={15} className="text-[#C5A059]" />
                {branches.find((b) => b.id === activeBranch)?.name || user?.branch_name || "Branch"}
              </div>
            )}
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-medium text-[#1A1A1A]">{user?.name}</span>
              <span className="text-[11px] text-[#8A6D2F]">{user?.role_name}</span>
            </div>
            <div className="h-9 w-9 rounded-full bg-[#C5A059] text-white flex items-center justify-center font-medium">
              {(user?.name || "?").charAt(0)}
            </div>
            <ChangePassword />
            <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn" title="Logout">
              <SignOut size={18} />
            </Button>
          </div>
        </header>
        <NoticeBar user={user} />
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
